/**
 * Students management page.
 * List with {@link SlidePanel} for create/edit forms.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/forms/Button';
import { SlidePanel } from '@/components/common/SlidePanel';
import { StudentsDataTable } from '@/components/students/inperson/StudentsDataTable';
import { CreateStudentView } from '@/components/students/inperson/CreateStudentView';
import { EditStudentView } from '@/components/students/inperson/EditStudentView';
import { ImportStudentsModal } from '@/components/students/inperson/ImportStudentsModal';
import { useDetailsView } from '@/hooks/useDetailsView';
import { useViewMode } from '@/hooks/useViewMode';
import { ManagementPage } from '@/components/common/ManagementPage';
import { usePageActions } from '@/hooks/usePageActions';

/** In-person students list with {@link SlidePanel} create/edit and CSV import. */
export const StudentsManagementPage: React.FC = () => {
  const { canCreate, canImportCsv, canEditRow, canDeleteRow, canExportCsv } =
    usePageActions('students');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [createFormOpen, setCreateFormOpen] = React.useState(false);
  const [importModalOpen, setImportModalOpen] = React.useState(false);
  const [isImporting, setIsImporting] = React.useState(false);
  const { viewMode, setViewMode } = useViewMode('students-management');
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();
  const [editStudentId, setEditStudentId] = React.useState<string | null>(null);

  /** Deep-link: open create/edit/view via `?create=` / `?edit=` / `?view=`, then strip the params. */
  React.useEffect(() => {
    const editId = searchParams.get('edit');
    const viewId = searchParams.get('view');
    const shouldCreate = searchParams.get('create') === 'true';

    if (editId) {
      setEditStudentId(editId);
      setCreateFormOpen(false);
      closeRow();
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }

    if (viewId) {
      setOpenRow(viewId);
      setCreateFormOpen(false);
      setEditStudentId(null);
      searchParams.delete('view');
      setSearchParams(searchParams, { replace: true });
    }

    if (shouldCreate) {
      setCreateFormOpen(true);
      setEditStudentId(null);
      closeRow();
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, closeRow, openRow]);

  /** Open the edit panel and close any expanded details row. */
  const handleEditStudent = (id: string) => {
    setEditStudentId(id);
    setCreateFormOpen(false);
    closeRow();
  };

  /** Close both create and edit panels. */
  const handleCloseForm = () => {
    setCreateFormOpen(false);
    setEditStudentId(null);
  };

  /** Close panels and refresh the list plus the edited student detail query. */
  const handleSuccess = () => {
    const savedStudentId = editStudentId;
    setCreateFormOpen(false);
    setEditStudentId(null);
    void queryClient.invalidateQueries({ queryKey: ['users', 'student'] });
    if (savedStudentId) {
      void queryClient.invalidateQueries({ queryKey: ['students', savedStudentId] });
    }
  };

  return (
    <ManagementPage
      title="Gestion des Élèves"
      subtitle="Créez, modifiez et gérez tous vos élèves"
      actions={
        canCreate || canImportCsv ? (
          <div className="flex items-center gap-3">
            {canImportCsv && (
              <Button
                preset="import-csv"
                onClick={() => setImportModalOpen(true)}
                disabled={isImporting}
              >
                Importer par CSV
              </Button>
            )}
            {canCreate && (
              <Button
                preset="create-entity"
                onClick={() => {
                  setCreateFormOpen(true);
                  setEditStudentId(null);
                  closeRow();
                }}
                disabled={isImporting}
              >
                Créer un élève
              </Button>
            )}
          </div>
        ) : undefined
      }
    >
      <StudentsDataTable
        expandedRowId={openRowId}
        onToggleRow={openRow}
        onCloseDetails={closeRow}
        onEditStudent={handleEditStudent}
        enableFilters={true}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        pageActions={{ canEditRow, canDeleteRow, canExportCsv }}
      />

      <SlidePanel isOpen={createFormOpen} onClose={handleCloseForm}>
        <CreateStudentView onClose={handleCloseForm} onSuccess={handleSuccess} />
      </SlidePanel>

      <SlidePanel isOpen={!!editStudentId} onClose={handleCloseForm}>
        {editStudentId && (
          <EditStudentView
            key={editStudentId}
            studentId={editStudentId}
            onClose={handleCloseForm}
            onSuccess={handleSuccess}
          />
        )}
      </SlidePanel>

      <ImportStudentsModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImportStart={() => setIsImporting(true)}
        onImportEnd={() => setIsImporting(false)}
      />
    </ManagementPage>
  );
};
